/**
 * Mobile-only bottom navigation bar (F16). Primary actions for small viewports.
 * Visible on md and below; fixed to bottom with safe-area padding.
 * Icons only (no under-label text) so labels are not truncated on narrow screens;
 * each control keeps aria-label and title for accessibility and hover tooltips.
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { navigateToHome, HOME_ROUTE_STATE } from '../../utils/homeNavigation';

function MessagesIcon({ active }) {
  return (
    <svg className="w-6 h-6" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

const navItems = [
  { id: 'home', path: '/', label: 'Home', icon: HomeIcon },
  { id: 'societies', path: '/societies', label: 'Societies', icon: SocietiesIcon },
  { id: 'events', path: '/events', label: 'Events', icon: EventsIcon },
  { id: 'messages', path: '/messages', label: 'Messages', icon: MessagesIcon },
  { id: 'profile', path: '/profile', label: 'Profile', icon: ProfileIcon },
];

function HomeIcon({ active }) {
  return (
    <svg className="w-6 h-6" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}

function SocietiesIcon({ active }) {
  return (
    <svg className="w-6 h-6" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function EventsIcon({ active }) {
  return (
    <svg className="w-6 h-6" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function ProfileIcon({ active }) {
  return (
    <svg className="w-6 h-6" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const isActive = (item) => {
    if (item.id === 'home') return location.pathname === '/';
    if (item.id === 'profile') return location.pathname === '/' && new URLSearchParams(location.search).get('auth') === 'profile';
    if (item.id === 'messages') return location.pathname === '/messages' || location.pathname.startsWith('/messages/');
    return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  };

  const handleClick = (item) => {
    if (item.id === 'profile') {
      if (isAuthenticated) navigate('/?auth=profile', { state: HOME_ROUTE_STATE });
      else navigate('/login');
    } else if (item.id === 'messages') {
      if (isAuthenticated) navigate('/messages');
      else navigate('/login');
    } else if (item.id === 'home') {
      navigateToHome(navigate);
    } else {
      navigate(item.path);
    }
  };

  return (
    <nav
      className="hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 safe-area-pb"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
      role="navigation"
      aria-label="Primary mobile navigation"
    >
      <div className="flex items-center justify-around h-12 min-h-[48px]">
        {navItems.map((item) => {
          const active = isActive(item);
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleClick(item)}
              title={item.label}
              className={`flex items-center justify-center flex-1 min-w-0 min-h-[48px] min-w-[48px] p-2 transition-colors ${
                active ? 'text-primary-600' : 'text-gray-500 hover:text-gray-700'
              }`}
              aria-current={active ? 'page' : undefined}
              aria-label={item.label}
            >
              <Icon active={active} />
            </button>
          );
        })}
      </div>
    </nav>
  );
}
